import React, { useEffect, useState } from "react";
import { X, ChevronDown, Calendar } from "lucide-react";
import { toast } from "react-toastify";
import { useAuth } from "../../../../firebase/AuthProvider";
import {
  getWaivers,
  GetParticipantByWaiverFamily,
  getParticipantInfo,
  editParticipant,
} from "../../../../services/participantServices/ParticipantService";
import { getAllRegions } from "../../../../services/userServices/UserInfoService";
import Waiver from "../../../../interfaces/participant/Waiver";
import { DefaultUSCountryCodeId } from "../../../../Data/SD";
import { UserWaiverDTO } from "../../../../interfaces/Participant";
import "./AddParticipantModal.css";

interface OptionType {
  value: string;
  label: string;
}

interface EditParticipantModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  participantId: number | null;
}

const EditParticipantModal: React.FC<EditParticipantModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  participantId,
}) => {
  const { currentUser } = useAuth();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isFetching, setIsFetching] = useState<boolean>(false);

  const [participantData, setParticipantData] = useState({
    firstName: "",
    lastName: "",
    participantEmail: "",
    caseworker: "",
    waiverId: "",
    region: 0,
    mobileNumber: "",
    countryCodeId: DefaultUSCountryCodeId,
    linkageDate: null as Date | null,
    cpocDate: null as Date | null,
    cpocEndDate: null as Date | null,
    certificationDate: null as Date | null,
    participantSsnLast4: "",
  });

  const [employees, setEmployees] = useState<UserWaiverDTO[] | null>([]);
  const [waivers, setWaivers] = useState<Waiver[] | null>([]);
  const [employeeOptions, setEmployeeOptions] = useState<OptionType[]>([]);
  const [waiverOptions, setWaiverOptions] = useState<OptionType[]>([]);
  const [regionOptions, setRegionOptions] = useState<OptionType[]>([]);
  const [errors, setErrors] = useState<{ [key: string]: string | undefined }>({});

  useEffect(() => {
    const fetchInfo = async () => {
      if (currentUser && isOpen) {
        const authToken: string = await currentUser.getIdToken();
        const waiversFetched = await getWaivers(authToken);
        if (waiversFetched.success) {
          setWaivers(waiversFetched.data);
          const formattedWaivers = waiversFetched.data.map((w: Waiver) => ({
            value: w.id.toString(),
            label: w.waiverName,
          }));
          setWaiverOptions(formattedWaivers);
        }
        GetAllRegions();
      }
    };
    fetchInfo();
  }, [currentUser, isOpen]);

  useEffect(() => {
    if (isOpen && participantId) {
      fetchParticipantData();
    }
  }, [isOpen, participantId, currentUser]);

  useEffect(() => {
    if (isOpen && participantData.waiverId) {
      GetEmployees();
    }
  }, [participantData.waiverId, isOpen]);

  const fetchParticipantData = async () => {
    if (!currentUser || !participantId) return;

    setIsFetching(true);
    try {
      const authToken = await currentUser.getIdToken();
      const response = await getParticipantInfo(participantId.toString(), authToken);

      if (response === "unauthorized") {
        toast.error("Unauthorized to view this participant");
        onClose();
        return;
      }

      if (response && response !== null) {
        const participant = response as any; // API returns more fields than interface
        setParticipantData({
          firstName: participant.firstName || "",
          lastName: participant.lastName || "",
          participantEmail: participant.participantEmail || "",
          caseworker: participant.assignee?.id?.toString() || "",
          waiverId: participant.waiverTypeId?.toString() || participant.waiver?.id?.toString() || "",
          region: participant.regionId || participant.region || 0,
          mobileNumber: participant.mobileNumber || "",
          countryCodeId: participant.countryId || DefaultUSCountryCodeId,
          linkageDate: participant.linkageDate ? new Date(participant.linkageDate) : null,
          cpocDate: participant.cpocDate ? new Date(participant.cpocDate) : null,
          cpocEndDate: participant.cpocEndDate ? new Date(participant.cpocEndDate) : null,
          certificationDate: participant.certificationDate ? new Date(participant.certificationDate) : null,
          participantSsnLast4: participant.participantSsnLast4 || "",
        });
      } else {
        toast.error("Failed to fetch participant data");
        onClose();
      }
    } catch (error: any) {
      toast.error(error?.message || "Failed to fetch participant data");
      onClose();
    } finally {
      setIsFetching(false);
    }
  };

  const GetAllRegions = async () => {
    const token = await currentUser?.getIdToken();
    if (!token) return;
    getAllRegions(token).then((res) => {
      if (res.success) {
        const formattedRegions = res.data.map((region: any) => ({
          value: region.regionId.toString(),
          label: region.regionName,
        }));
        setRegionOptions(formattedRegions);
      }
    });
  };

  const GetEmployees = async () => {
    const token = await currentUser?.getIdToken();
    if (!token || !participantData.waiverId) return;

    const waiverCode = waivers?.find(
      (w) => w.id.toString() === participantData.waiverId
    )?.waiverName;

    if (waiverCode) {
      const employeesFetched = await GetParticipantByWaiverFamily(
        token,
        waiverCode
      );
      if (employeesFetched.success) {
        setEmployees(employeesFetched.data);
        const formattedEmployees = employeesFetched.data.map(
          (emp: UserWaiverDTO) => ({
            value: emp.id.toString(),
            label: emp.displayName,
          })
        );
        setEmployeeOptions(formattedEmployees);
      }
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name } = e.target;
    let value = (e.target as HTMLInputElement).value;

    if (name === "firstName" || name === "lastName") {
      // Remove any whitespace characters
      value = value.replace(/\s+/g, "");
    } else if (name === "participantEmail") {
      // Remove whitespace from email
      value = value.replace(/\s+/g, "");
    } else if (name === "mobileNumber") {
      // Allow digits only and cap to 10 digits
      value = value.replace(/\D/g, "").slice(0, 10);
    } else if (name === "participantSsnLast4") {
      // Allow digits only and cap to 4 digits
      value = value.replace(/\D/g, "").slice(0, 4);
    }

    setParticipantData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const dateValue = value ? new Date(value) : null;
    setParticipantData((prev) => ({
      ...prev,
      [name]: dateValue,
    }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const handleWaiverChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedId = e.target.value;
    setParticipantData((prev) => ({
      ...prev,
      waiverId: selectedId || "",
      caseworker: "", // Reset caseworker when waiver changes
    }));
    setEmployeeOptions([]);
    setErrors((prev) => ({ ...prev, waiverId: undefined, caseworker: undefined }));
  };

  const handleAssigneeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setParticipantData((prev) => ({
      ...prev,
      caseworker: e.target.value || "",
    }));
    if (errors.caseworker) {
      setErrors((prev) => ({ ...prev, caseworker: undefined }));
    }
  };

  const handleRegionChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setParticipantData((prev) => ({
      ...prev,
      region: e.target.value ? parseInt(e.target.value) : 0,
    }));
    if (errors.region) {
      setErrors((prev) => ({ ...prev, region: undefined }));
    }
  };

  const isValidEmail = (email: string): boolean => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const isValidUSPhone = (phone: string): boolean => {
    return /^\d{10}$/.test(phone.replace(/\D/g, ""));
  };

  const validateFields = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    if (participantData.firstName.trim() === "") {
      newErrors.firstName = "First Name is required.";
    }
    if (participantData.lastName.trim() === "") {
      newErrors.lastName = "Last Name is required.";
    }
    if (participantData.participantEmail) {
      if (!isValidEmail(participantData.participantEmail)) {
        newErrors.participantEmail = "Invalid email address.";
      }
    }
    if (participantData.mobileNumber) {
      if (!isValidUSPhone(participantData.mobileNumber)) {
        newErrors.mobileNumber = "Phone number must be 10 digits (US).";
      }
    }
    if (participantData.waiverId === "") {
      newErrors.waiverId = "Participant Waiver is required.";
    }
    // if (participantData.caseworker === "") {
    //   newErrors.caseworker = "Support Coordinator is required.";
    // }
    if (participantData.region === 0) {
      newErrors.region = "Region is required.";
    }
    if (!participantData.linkageDate) {
      newErrors.linkageDate = "Linkage Date is required.";
    }
    if (participantData.participantSsnLast4 && participantData.participantSsnLast4 !== "") {
      if (!/^\d{4}$/.test(participantData.participantSsnLast4)) {
        newErrors.participantSsnLast4 = "SSN must be exactly 4 digits.";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateFields() || !participantId) {
      return;
    }

    setIsLoading(true);
    try {
      const authToken = await currentUser?.getIdToken();
      if (!authToken) {
        toast.error("Authentication error");
        return;
      }

      // UPDATE THIS CALL
      const response = await editParticipant(
        {
          id: participantId,
          participantId: participantId,
          firstName: participantData.firstName,
          lastName: participantData.lastName,
          email: participantData.participantEmail,
          mobileNumber: participantData.mobileNumber,
          waiverId: parseInt(participantData.waiverId),
          regionId: participantData.region,
          countryId: participantData.countryCodeId,
          participantSsnLast4: participantData.participantSsnLast4,
          linkageDate: participantData.linkageDate,
          cpocDate: participantData.cpocDate,
          cpocEndDate: participantData.cpocEndDate,
          certificationDate: participantData.certificationDate,
        },
        authToken
      );

      if (response.success) {
        toast.success("Participant updated successfully!");
        handleCancel();
        if (onSuccess) {
          onSuccess();
        }
      } else {
        toast.error(response.message || "Failed to update participant");
      }
    } catch (error: any) {
      toast.error(error?.message || "Failed to update participant");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setParticipantData({
      firstName: "",
      lastName: "",
      participantEmail: "",
      caseworker: "",
      waiverId: "",
      region: 0,
      mobileNumber: "",
      countryCodeId: DefaultUSCountryCodeId,
      linkageDate: null,
      cpocDate: null,
      cpocEndDate: null,
      certificationDate: null,
      participantSsnLast4: "",
    });
    setErrors({});
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      <div
        className="fixed inset-0 z-[100] flex items-center justify-center bg-black bg-opacity-50 overflow-y-auto no-scrollbar p-4"
        onClick={handleCancel}
      >
        <div
          className="relative w-full max-w-[55rem] my-auto rounded-2xl bg-white shadow-xl overflow-y-auto no-scrollbar"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="sticky top-0 bg-white rounded-t-2xl z-10">
            <div className="flex items-center justify-between border-b border-[#E5E7EB] py-4 px-6">
              <div>
                <h2 className="" style={{
                  color: "#0A0A0A",
                  fontFamily: "Poppins",
                  fontSize: "1.25rem",
                  fontWeight: 700,
                  lineHeight: "1.75rem"
                }}>
                  Edit Participant
                </h2>
                <p className="m-0 mt-1 text-[#4A5565] font-poppins font-normal" style={{ fontSize: "0.875rem", fontWeight: 400, lineHeight: "1.25rem" }}>
                  Edit participant info
                </p>
              </div>
              <button
                onClick={handleCancel}
                className="flex items-center text-[#0A0A0A] hover:text-gray-600 transition-colors"
              >
                <X size={20} />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="px-6 py-4">
            {isFetching ? (
              <div className="text-center p-8">
                <p className="text-[#6B7280]">Loading participant data...</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Participant First Name */}
                  <div>
                    <label className="block text-base font-normal text-[#364153] mb-2">
                      Participant First Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="firstName"
                      onKeyDown={(e) => { if (e.key === " ") e.preventDefault(); }}
                      value={participantData.firstName}
                      onChange={handleChange}
                      placeholder="First Name"
                      className={`w-full px-3 py-2 border ${errors.firstName
                        ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                        : "border-[#D1D5DC] focus:border-purple-500 focus:ring-purple-500"
                        } rounded-[0.625rem] text-base placeholder:text-gray-400 focus:outline-none focus:ring-1`}
                    />
                    {errors.firstName && (
                      <p className="text-red-500 text-xs mt-1">{errors.firstName}</p>
                    )}
                  </div>

                  {/* Participant Last Name */}
                  <div>
                    <label className="block text-base font-normal text-[#364153] mb-2">
                      Participant Last Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="lastName"
                      value={participantData.lastName}
                      onChange={handleChange}
                      placeholder="Last Name"
                      className={`w-full px-3 py-2 border ${errors.lastName
                        ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                        : "border-[#D1D5DC] focus:border-purple-500 focus:ring-purple-500"
                        } rounded-[0.625rem] text-base placeholder:text-gray-400 focus:outline-none focus:ring-1`}
                    />
                    {errors.lastName && (
                      <p className="text-red-500 text-xs mt-1">{errors.lastName}</p>
                    )}
                  </div>

                  {/* Participant Email */}
                  <div>
                    <label className="block text-base font-normal text-[#364153] mb-2">
                      Participant Email <span className="text-[#6B7280]">(optional)</span>
                    </label>
                    <input
                      type="email"
                      name="participantEmail"
                      onKeyDown={(e) => { if (e.key === " ") e.preventDefault(); }}
                      value={participantData.participantEmail || ""}
                      onChange={handleChange}
                      placeholder="Email"
                      className={`w-full px-3 py-2 border ${errors.participantEmail
                        ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                        : "border-[#D1D5DC] focus:border-purple-500 focus:ring-purple-500"
                        } rounded-[0.625rem] text-base placeholder:text-gray-400 focus:outline-none focus:ring-1`}
                    />
                    {errors.participantEmail && (
                      <p className="text-red-500 text-xs mt-1">{errors.participantEmail}</p>
                    )}
                  </div>

                  {/* Mobile Telephone Number */}
                  <div>
                    <label className="block text-base font-normal text-[#364153] mb-2">
                      Mobile Telephone Number <span className="text-[#6B7280]">(optional)</span>
                    </label>
                    <input
                      type="text"
                      name="mobileNumber"
                      inputMode="numeric"
                      maxLength={10}
                      pattern="\d{10}"
                      value={participantData.mobileNumber || ""}
                      onChange={handleChange}
                      placeholder="Mobile Telephone Number"
                      className={`w-full px-3 py-2 border ${errors.mobileNumber
                        ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                        : "border-[#D1D5DC] focus:border-purple-500 focus:ring-purple-500"
                        } rounded-[0.625rem] text-base placeholder:text-gray-400 focus:outline-none focus:ring-1`}
                    />
                    {errors.mobileNumber && (
                      <p className="text-red-500 text-xs mt-1">{errors.mobileNumber}</p>
                    )}
                  </div>

                  {/* Participant Waiver */}
                  <div>
                    <label className="block text-base font-normal text-[#364153] mb-2">
                      Participant Waiver <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <select
                        value={participantData.waiverId || ""}
                        onChange={handleWaiverChange}
                        className={`w-full px-3 py-2 border ${errors.waiverId
                          ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                          : "border-[#D1D5DC] focus:border-purple-500 focus:ring-purple-500"
                          } rounded-[0.625rem] text-base focus:outline-none focus:ring-1 appearance-none bg-white`}
                      >
                        <option value="">Select waiver</option>
                        {waivers?.map((w) => (
                          <option key={w.id} value={w.id.toString()}>
                            {w.waiverName}
                          </option>
                        ))}
                      </select>
                      <ChevronDown
                        size={16}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
                      />
                    </div>
                    {errors.waiverId && (
                      <p className="text-red-500 text-xs mt-1">{errors.waiverId}</p>
                    )}
                  </div>

                  {/* Support Coordinator */}
                  {/* <div>
                    <label className="block text-base font-normal text-[#364153] mb-2">
                      Support Coordinator<span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <select
                        value={participantData.caseworker || ""}
                        onChange={handleAssigneeChange}
                        disabled={!participantData.waiverId}
                        className={`w-full px-3 py-2 border ${errors.caseworker
                          ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                          : "border-[#D1D5DC] focus:border-purple-500 focus:ring-purple-500"
                          } rounded-[0.625rem] text-base focus:outline-none focus:ring-1 appearance-none bg-white ${!participantData.waiverId ? "bg-gray-100 cursor-not-allowed" : ""
                          }`}
                      >
                        <option value="">Select support coordinator</option>
                        {employeeOptions.map((emp) => (
                          <option key={emp.value} value={emp.value}>
                            {emp.label}
                          </option>
                        ))}
                      </select>
                      <ChevronDown
                        size={16}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
                      />
                    </div>
                    {errors.caseworker && (
                      <p className="text-red-500 text-xs mt-1">{errors.caseworker}</p>
                    )}
                  </div> */}

                  {/* Region */}
                  <div>
                    <label className="block text-base font-normal text-[#364153] mb-2">
                      Region <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <select
                        value={participantData.region || ""}
                        onChange={handleRegionChange}
                        className={`w-full px-3 py-2 border ${errors.region
                          ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                          : "border-[#D1D5DC] focus:border-purple-500 focus:ring-purple-500"
                          } rounded-[0.625rem] text-base focus:outline-none focus:ring-1 appearance-none bg-white`}
                      >
                        <option value="">Select region</option>
                        {regionOptions.map((region) => (
                          <option key={region.value} value={region.value}>
                            {region.label}
                          </option>
                        ))}
                      </select>
                      <ChevronDown
                        size={16}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
                      />
                    </div>
                    {errors.region && (
                      <p className="text-red-500 text-xs mt-1">{errors.region}</p>
                    )}
                  </div>

                  {/* Linkage Date */}
                  <div>
                    <label className="block text-base font-normal text-[#364153] mb-2">
                      Linkage Date <span className="text-red-500">*</span>
                    </label>

                    <div className="relative">
                      <input
                        type="date"
                        name="linkageDate"
                        value={
                          participantData.linkageDate
                            ? new Date(participantData.linkageDate)
                              .toISOString()
                              .split("T")[0]
                            : ""
                        }
                        onChange={handleDateChange}
                        placeholder="12/25/2025"
                        className={`w-full px-3 py-2 pr-10 border ${errors.linkageDate
                          ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                          : "border-[#D1D5DC] focus:border-purple-500 focus:ring-purple-500"
                          } rounded-[0.625rem] text-base focus:outline-none focus:ring-1`}
                      />

                      <Calendar
                        size={18}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
                      />
                    </div>

                    {errors.linkageDate && (
                      <p className="text-red-500 text-xs mt-1">{errors.linkageDate}</p>
                    )}
                  </div>

                  {/* Certification Date */}
                  <div>
                    <label className="block text-base font-normal text-[#364153] mb-2">
                      Certification Date <span className="text-[#6B7280]">(optional)</span>
                    </label>
                    <div className="relative">
                      <input
                        type="date"
                        name="certificationDate"
                        value={
                          participantData.certificationDate
                            ? new Date(participantData.certificationDate).toISOString().split("T")[0]
                            : ""
                        }
                        onChange={handleDateChange}
                        placeholder="12/25/2025"
                        className="w-full px-3 py-2 pr-10 border border-[#D1D5DC] rounded-[0.625rem] text-base focus:outline-none focus:ring-1 focus:border-purple-500 focus:ring-purple-500"
                      />
                      <Calendar
                        size={18}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
                      />
                    </div>
                  </div>

                  {/* CPOC Start Date */}
                  <div>
                    <label className="block text-base font-normal text-[#364153] mb-2">
                      CPOC Start Date <span className="text-[#6B7280]">(optional)</span>
                    </label>
                    <div className="relative">
                      <input
                        type="date"
                        name="cpocDate"
                        value={
                          participantData.cpocDate
                            ? new Date(participantData.cpocDate).toISOString().split("T")[0]
                            : ""
                        }
                        onChange={handleDateChange}
                        placeholder="12/25/2025"
                        className="w-full px-3 py-2 pr-10 border border-[#D1D5DC] rounded-[0.625rem] text-base focus:outline-none focus:ring-1 focus:border-purple-500 focus:ring-purple-500"
                      />
                      <Calendar
                        size={18}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
                      />
                    </div>
                  </div>

                  {/* CPOC End Date */}
                  <div>
                    <label className="block text-base font-normal text-[#364153] mb-2">
                      CPOC End Date <span className="text-[#6B7280]">(optional)</span>
                    </label>
                    <div className="relative">
                      <input
                        type="date"
                        name="cpocEndDate"
                        value={
                          participantData.cpocEndDate
                            ? new Date(participantData.cpocEndDate).toISOString().split("T")[0]
                            : ""
                        }
                        onChange={handleDateChange}
                        placeholder="12/25/2025"
                        className="w-full px-3 py-2 pr-10 border border-[#D1D5DC] rounded-[0.625rem] text-base focus:outline-none focus:ring-1 focus:border-purple-500 focus:ring-purple-500"
                      />
                      <Calendar
                        size={18}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
                      />
                    </div>
                  </div>

                  {/* Participant SSN Last 4 Digit */}
                  <div>
                    <label className="block text-base font-normal text-[#364153] mb-2">
                      Participant SSN Last 4 Digit <span className="text-[#6B7280]">(optional)</span>
                    </label>
                    <input
                      type="text"
                      name="participantSsnLast4"
                      inputMode="numeric"
                      maxLength={4}
                      pattern="\d{4}"
                      value={participantData.participantSsnLast4 || ""}
                      onChange={handleChange}
                      placeholder="Last 4 digits of SSN"
                      className={`w-full px-3 py-2 border ${errors.participantSsnLast4
                        ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                        : "border-[#D1D5DC] focus:border-purple-500 focus:ring-purple-500"
                        } rounded-[0.625rem] text-base placeholder:text-gray-400 focus:outline-none focus:ring-1`}
                    />
                    {errors.participantSsnLast4 && (
                      <p className="text-red-500 text-xs mt-1">{errors.participantSsnLast4}</p>
                    )}
                  </div>
                </div>

                {/* Submit Button */}
                <div className="flex justify-end mt-6 pt-4 border-t border-[#E5E7EB]">
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="px-6 py-2 bg-[#7E57C2] text-white font-poppins text-base font-medium rounded-[0.625rem] hover:bg-[#6D4CAB] transition-colors"
                  >
                    {isLoading ? "Submitting..." : "Submit"}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div >
    </>
  );
};

export default EditParticipantModal;

